import React, { useState } from 'react';
import { usePOSStore } from '../../../stores/pos.store';
import { useStaff, useBaseCurrency, useActiveRate } from '../../../db/hooks/useLookupData';
import { User, Percent } from 'lucide-react';
import { CustomSelect } from '../../common/CustomSelect';

export function POSTipStep() {
  const {
    cartItems,
    discount,
    tip,
    tipPercentage,
    tipOperatorId,
    setTip,
  } = usePOSStore();
  const { staff = [] } = useStaff();
  const { currencySymbol } = useBaseCurrency();
  const { activeRate } = useActiveRate();

  const subtotal = cartItems.reduce((acc, item) => acc + item.unitPrice * item.quantity, 0);
  const neto = Math.max(0, subtotal - discount);

  // Default operator: first item's operator or first staff in list
  const defaultStaffId = cartItems.find((i) => i.operatorId)?.operatorId || staff[0]?.id || '';
  const [selectedStaffId, setSelectedStaffId] = useState<string>(tipOperatorId || defaultStaffId);
  const [customTipInput, setCustomTipInput] = useState<string>(tip > 0 && !tipPercentage ? String(tip) : '');
  const [isCustomMode, setIsCustomMode] = useState(tip > 0 && !tipPercentage);

  const selectedStaff = staff.find((p) => p.id === selectedStaffId);
  const rateVal = activeRate?.usd || 1;

  const handleSelectPercentage = (pct: number) => {
    setIsCustomMode(false);
    setCustomTipInput('');
    if (pct === 0) {
      setTip(0, null, null);
    } else {
      const calculatedTip = Math.round(((neto * pct) / 100 + Number.EPSILON) * 100) / 100;
      setTip(calculatedTip, pct, selectedStaffId);
    }
  };

  const handleCustomTipChange = (valStr: string) => {
    setCustomTipInput(valStr);
    const num = parseFloat(valStr);
    if (!isNaN(num) && num >= 0) {
      setTip(num, null, selectedStaffId);
    } else {
      setTip(0, null, selectedStaffId);
    }
  };

  return (
    <div className="flex-1 flex flex-col p-3.5 sm:p-6 space-y-3.5 sm:space-y-6 pb-24 lg:pb-6 animate-fade-in overflow-y-auto custom-scrollbar">

      {/* Staff Selector */}
      <div className="space-y-1.5 sm:space-y-2">
        <label className="block text-xs font-bold text-text-primary flex items-center gap-1.5">
          <User className="w-3.5 h-3.5 text-brand-sky" />
          Atribuir propina a:
        </label>
        <CustomSelect
          value={selectedStaffId}
          onChange={(val) => {
            setSelectedStaffId(val);
            if (tip > 0) {
              setTip(tip, tipPercentage, val);
            }
          }}
          options={staff.map((p) => ({ value: p.id, label: `${p.name} (${p.role || 'Esteticista'})` }))}
        />
      </div>

      {/* Preset Percentage Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3">
        {[0, 10, 18, 25].map((pct) => {
          const tipValue = Math.round(((neto * pct) / 100 + Number.EPSILON) * 100) / 100;
          const isSelected = !isCustomMode && (pct === 0 ? tip === 0 : tipPercentage === pct);

          return (
            <button
              key={pct}
              type="button"
              onClick={() => handleSelectPercentage(pct)}
              className={`p-3.5 sm:p-5 rounded-2xl border flex flex-col items-center justify-center gap-1 transition-all cursor-pointer ${
                isSelected
                  ? 'bg-brand-orange/15 border-brand-orange text-brand-orange ring-2 ring-brand-orange/30 shadow-md'
                  : 'bg-surface border-border text-text-primary hover:border-brand-sky hover:bg-surface-secondary'
              }`}
            >
              <span className="text-lg font-black">
                {pct === 0 ? 'Sin propina' : `${pct}%`}
              </span>
              {pct > 0 && (
                <span className="text-xs font-extrabold text-text-secondary">
                  {currencySymbol}{tipValue.toFixed(2)}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Custom Tip Input */}
      <div className="bg-surface border border-border p-4 rounded-2xl space-y-3">
        <button
          type="button"
          onClick={() => {
            setIsCustomMode(true);
            setTip(0, null, selectedStaffId);
          }}
          className={`w-full flex items-center justify-between text-xs font-bold px-3 py-2 rounded-xl transition-colors ${
            isCustomMode
              ? 'bg-brand-sky/15 text-brand-sky border border-brand-sky/30'
              : 'text-text-secondary hover:text-text-primary hover:bg-surface-secondary'
          }`}
        >
          <span className="flex items-center gap-2">
            <Percent className="w-4 h-4" /> Propina personalizada
          </span>
          <span className="text-[11px] underline">Ingresar monto libre</span>
        </button>

        {isCustomMode && (
          <div className="pt-2 flex items-center gap-3">
            <div className="relative flex-1">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 font-bold text-text-tertiary text-sm">
                {currencySymbol}
              </span>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={customTipInput}
                onChange={(e) => handleCustomTipChange(e.target.value)}
                placeholder="0.00"
                className="w-full pl-8 pr-4 py-2.5 rounded-xl border border-border bg-surface text-sm font-bold text-text-primary focus:border-brand-sky focus:ring-2 focus:ring-brand-sky/20"
              />
            </div>
            <span className="text-xs font-bold text-text-secondary">
              ≈ Bs.S {((parseFloat(customTipInput) || 0) * rateVal).toFixed(2)}
            </span>
          </div>
        )}
      </div>

      {/* Tip Attribution Summary */}
      {tip > 0 && (
        <div className="bg-emerald-500/10 border border-emerald-500/20 p-4 rounded-2xl flex items-center justify-between animate-fade-in">
          <div>
            <span className="text-xs text-emerald-600 font-bold block">
              Propina asignada a {selectedStaff?.name || 'Esteticista'}
            </span>
            <span className="text-xs text-text-tertiary">
              Se agregará al total a cobrar
            </span>
          </div>
          <div className="text-right font-black text-lg text-emerald-500">
            +{currencySymbol}{tip.toFixed(2)}
          </div>
        </div>
      )}
    </div>
  );
}
